import React, { useMemo } from 'react';
import * as R from 'ramda';
import classNames from 'classnames';
import { Stepper, Step, StepLabel, StepIconProps } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { upperFirstLetter } from '@alycecom/utils';
import { AlyceTheme } from '@alycecom/ui';

import StatusStepIcon from './StatusStepIcon';
import StatusesConnector from './StatusesConnector';

const useStyles = makeStyles<AlyceTheme, { showStatuses: boolean }>(({ palette, spacing, breakpoints }) => ({
  stepper: {
    padding: 0,
    overflowX: 'hidden',
    [breakpoints.down('sm')]: {
      position: 'absolute',
      width: '100%',
    },
  },
  step: {
    width: 64,
    [breakpoints.down('sm')]: {
      width: 36,
      paddingRight: 2,
      paddingLeft: 2,
    },
    paddingRight: 0,
    paddingLeft: 0,
  },
  stepLabelContainer: ({ showStatuses }) => ({
    '& > span': {
      color: palette.common.black,
      fontSize: 12,
    },
    '& > span.MuiStepLabel-label.MuiStepLabel-alternativeLabel': {
      marginTop: showStatuses ? spacing(1) : 0,
    },
  }),
  completedLabel: {
    fontWeight: 'normal !important',
  },
  passedLabel: {
    '& > span': {
      color: `${palette.grey.main} !important`,
    },
  },
}));

export interface IGiftStatusBarProps {
  statuses?: { id: number; icon: string; name: string }[];
  activeStatus?: number | null;
  showStatuses?: boolean;
}

const GiftStatusBar = ({
  statuses = [],
  activeStatus = null,
  showStatuses = true,
}: IGiftStatusBarProps): JSX.Element => {
  const classes = useStyles({ showStatuses });

  const activeStep = useMemo(() => R.findIndex(R.propEq('id', activeStatus))(statuses), [statuses, activeStatus]);

  return (
    <Stepper
      className="stepper-ie"
      classes={{ root: classes.stepper }}
      alternativeLabel
      activeStep={activeStep}
      connector={<StatusesConnector />}
      data-testid="Header.GiftStatusBar"
    >
      {statuses.map(({ id, icon, name }) => {
        const stepLabel = upperFirstLetter(name);
        const isCurrentStep = id === activeStatus;
        return (
          <Step classes={{ root: classes.step }} key={id}>
            <StepLabel
              classes={{
                labelContainer: classNames(classes.stepLabelContainer, { [classes.passedLabel]: !isCurrentStep }),
                completed: classes.completedLabel,
              }}
              StepIconComponent={StatusStepIcon}
              StepIconProps={{ emoji: icon } as Partial<StepIconProps>}
              data-testid={`Header.GiftStatusBar.${stepLabel}`}
            >
              {showStatuses ? stepLabel : ''}
            </StepLabel>
          </Step>
        );
      })}
    </Stepper>
  );
};

export default GiftStatusBar;
